import { Fragment, useEffect, useMemo, useState } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Ac2AccessGroupModel, Ac2AccessGroupRequest, visitorAccessType } from "src/api/access/ac-group-types";
import { BaseButton, BaseInput, BaseRadio, BaseToggle, BaseTooltip } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import { visitorSettings } from "../../const";
import { useRecoilValue } from "recoil";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PaPartner } from "src/types/partner";

type Props = {
  accessGroup?: Ac2AccessGroupModel;
  control?: Control<Ac2AccessGroupRequest>;
  watch: UseFormWatch<Ac2AccessGroupRequest>;
  onChangeEdit?: (type: "setting") => void;
  setValue: UseFormSetValue<Ac2AccessGroupRequest>;
};

// 층수 입력값(콤마 구분 문자열) <-> 배열 변환. blur 시점에만 form 값에 반영해서 입력 중 콤마가 사라지는 문제를 방지
const AccessibleFloorsInput = ({ value, onChange, disabled }: { value?: string[]; onChange: (value: string[]) => void; disabled?: boolean }) => {
  const [text, setText] = useState(value?.join(",") || "");

  useEffect(() => {
    setText(value?.join(",") || "");
  }, [value]);

  return (
    <BaseInput
      value={text}
      onChange={(inputValue: string) => setText(inputValue)}
      onBlur={(e) => {
        const floors = e.target.value
          .split(",")
          .map((floor) => floor.trim())
          .filter((floor) => floor !== "");

        // 중복제거 처리
        onChange(Array.from(new Set(floors)));
      }}
      placeholder="입력해주세요."
      disabled={disabled}
      maxLength={10000}
    />
  );
};

const AccessGroupSettingForm = ({ accessGroup, control, watch, onChangeEdit, setValue }: Props) => {
  const partner = useRecoilValue(globalPartnerState);

  // 센트럴 > 파트너 feature  > FEATURE_ACCESS_GROUP_VISITOR_APPROVAL 여부 판단
  const isVisitorApproval = useMemo(() => {
    return partner?.features?.some((feature: PaPartner) => feature.name === "FEATURE_ACCESS_GROUP_VISITOR_APPROVAL" && feature.active) || false;
  }, [partner]);

  // 파트너 feature > CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS 여부 판단 (엘리베이터 층수 제어 노출 여부)
  const isFloorAccessFeatureActive = useMemo(() => {
    return partner?.features?.some((feature: PaPartner) => feature.name === "CTRLROOM_ACCESS_GROUP_ACCESS_FLOORS" && feature.active) || false;
  }, [partner]);

  return (
    <article className="contents-container__wrap-article">
      <div className="flex-center-between">
        <BaseSectionTitle title={"설정"} />
        {accessGroup && (
          <div className="flex-center">
            {onChangeEdit && <BaseButton title="취소" className="size-small color-white mr10" onClick={() => onChangeEdit("setting")} />}
            <BaseButton title="저장" className="size-small" type="submit" />
          </div>
        )}
      </div>
      <section className="contents-container__grid">
        <div className="contents-container__grid-index">
          <div className="flex-center">
            <p>이용자 출입 가능 여부</p>
            <BaseTooltip className="ml2" contents="비활성화 하는 경우, 해당 출입그룹 - 출입장치에 이용자가 출입할 수 없습니다." touchIcon="QUESTION" />
          </div>
        </div>
        <div className="contents-container__grid-contents">
          <div className="minmax400">
            <Controller
              control={control}
              name="memberAccessYn"
              render={(
                { field: { onChange, value } }, //
              ) => <BaseToggle checked={value || false} onChange={() => onChange(!value)} />}
            />
          </div>
        </div>
      </section>
      <section className="contents-container__grid">
        <div className="contents-container__grid-index">
          <div className="flex-center">
            <p>방문자 초대 가능 여부</p>
            <BaseTooltip className="ml2" contents="비활성화 하는 경우, 해당 출입그룹에 방문자를  초대할 수 없습니다." touchIcon="QUESTION" />
          </div>
        </div>
        <div className="contents-container__grid-contents">
          <div className="minmax400">
            <Controller
              control={control}
              name="visitorAccessYn"
              render={(
                { field: { onChange, value } }, //
              ) => (
                <BaseToggle
                  checked={value}
                  onChange={(checked: boolean) => {
                    onChange(checked);

                    if (checked) {
                      setValue?.("visitorAccessGroupSelectableYn", true);
                      setValue?.("visitorInvitationApprovalYn", false);
                      setValue?.("visitorAccessType", visitorAccessType.VISITOR_MUTABLE);
                    } else {
                      setValue?.("visitorAccessGroupSelectableYn", false);
                      setValue?.("visitorInvitationApprovalYn", false);
                      setValue?.("visitorAccessType", visitorAccessType.VISITOR_MUTABLE);
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </section>
      <section className="contents-container__grid">
        <div className="contents-container__grid-index"></div>
        <div className="contents-container__grid-contents">
          <section className="contents-container__grid gray-box mt10 mb30" style={{ height: 180 }}>
            {visitorSettings.map((group) => (
              <Fragment key={group.name}>
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p>{group.title}</p>
                    <BaseTooltip className="ml2" contents={group.tooltip} touchIcon="QUESTION" />
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-center-start flex-row">
                    <Controller
                      control={control}
                      name={group.name as keyof Ac2AccessGroupRequest}
                      defaultValue={group.defaultValue}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <>
                            {group.options.map((option, index) => (
                              <BaseRadio
                                key={index}
                                id={`${group.name}-${index}`}
                                name={group.name}
                                label={option.label}
                                value={option.value as string}
                                checked={value === option.value}
                                className={index < group.options.length - 1 ? "mr10" : ""}
                                onChange={() => onChange(option.value)}
                                disabled={
                                  !watch("visitorAccessYn") ||
                                  (watch("visitorAccessYn") && group.name === "visitorInvitationApprovalYn" && !isVisitorApproval)
                                }
                              />
                            ))}
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </Fragment>
            ))}
          </section>
        </div>
      </section>
      {isFloorAccessFeatureActive && (
        <>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <div className="flex-center">
                <p>엘리베이터 연동</p>
                <BaseTooltip className="ml2" touchIcon="QUESTION">
                  <ul className="base-list">
                    <li>이 출입그룹으로 누를 수 있는 엘리베이터 층 버튼을 입력하세요.</li>
                    <li>설치 시 등록된 층 이름과 정확히 일치해야 합니다. (예: B1,1,2,3)</li>
                  </ul>
                </BaseTooltip>
              </div>
            </div>
            <div className="contents-container__grid-contents">
              <div className="minmax400">
                <Controller
                  control={control}
                  name="floorAccessYn"
                  render={(
                    { field: { onChange, value } }, //
                  ) => <BaseToggle checked={value || false} onChange={() => onChange(!value)} />}
                />
              </div>
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index"></div>
            <div className="contents-container__grid-contents">
              <section className="contents-container__grid gray-box">
                <div className="contents-container__grid-index">
                  <p>이용 가능 층</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax370">
                    <Controller
                      control={control}
                      name="accessibleFloors"
                      render={({ field: { onChange, value } }) => (
                        <AccessibleFloorsInput value={value} onChange={onChange} disabled={!watch("floorAccessYn")} />
                      )}
                    />
                  </div>
                </div>
              </section>
            </div>
          </section>
        </>
      )}
    </article>
  );
};

export default AccessGroupSettingForm;
